import { useRef, useState } from "react";

import { useNotification } from "@refinedev/core";

import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";

import {
  type ExportMoodChatLogRunsOptions,
  exportMoodChatLogRuns,
} from "../api";
import { buildExportFilename, buildMoodChatLogsCsv } from "../export-csv";
import type { MoodChatRunFilters } from "../types";

type UseMoodChatLogsExportOptions = Pick<
  ExportMoodChatLogRunsOptions,
  "fetchPage" | "maxPages"
>;

export interface MoodChatLogsExport {
  exportCsv: () => Promise<void>;
  isExporting: boolean;
  /** null while idle; the running count of collected runs while exporting. */
  exportedRunCount: number | null;
}

/**
 * Collects every run matching `filters` and downloads it as CSV.
 *
 * Callers pass the *applied* filters, never the draft ones, so the file always
 * matches the table on screen. The Limit field is ignored — it sizes the on-screen
 * page, and honouring it here would silently shorten the export.
 */
export function useMoodChatLogsExport(
  apiUrl: string,
  filters: MoodChatRunFilters,
  options: UseMoodChatLogsExportOptions = {},
): MoodChatLogsExport {
  const { open } = useNotification();
  const [exportedRunCount, setExportedRunCount] = useState<number | null>(null);
  // A ref, not the state: two clicks in the same tick would both read a stale
  // `exportedRunCount` and start two exports.
  const isExportingRef = useRef(false);

  async function exportCsv() {
    if (isExportingRef.current) return;

    isExportingRef.current = true;
    setExportedRunCount(0);

    try {
      const { runs, truncated } = await exportMoodChatLogRuns(apiUrl, filters, {
        ...options,
        onProgress: setExportedRunCount,
      });

      if (runs.length === 0) {
        open?.({
          type: "success",
          message: "No runs matched these filters",
          description:
            "Nothing was downloaded. Try widening the date range or clearing a filter.",
        });
        return;
      }

      downloadCsv(buildMoodChatLogsCsv(runs), buildExportFilename(filters));

      open?.({
        type: "success",
        message: `Exported ${runs.length.toLocaleString()} runs`,
        description: truncated
          ? "Stopped at the export cap — narrow the date range to get the rest."
          : "Grouped by conversation, one row per turn.",
      });
    } catch (error) {
      // Deliberately no partial file: a short CSV would be read as the whole truth.
      open?.({
        type: "error",
        message: "Export failed",
        description: toHumanErrorMessage(error),
      });
    } finally {
      isExportingRef.current = false;
      setExportedRunCount(null);
    }
  }

  return {
    exportCsv,
    isExporting: exportedRunCount !== null,
    exportedRunCount,
  };
}

function downloadCsv(csv: string, filename: string) {
  const url = URL.createObjectURL(
    new Blob([csv], { type: "text/csv;charset=utf-8" }),
  );
  const link = document.createElement("a");

  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
